import React, { useEffect, useState } from 'react'
import userflow from 'userflow.js'
import {
  WIDGET_BUTTON_BOTTOM,
  WIDGET_BUTTON_COLOR,
  WIDGET_BUTTON_COLOR_HOVER,
  WIDGET_BUTTON_SHADOW,
  WIDGET_BUTTON_SIZE,
  WIDGET_PANEL_BOTTOM
} from 'shared/util/supportWidgetButton'

/**
 * Gives the Userflow resource center the same round blue close button that HubSpot chat and the
 * GitBook docs embed get, so all three dismiss the same way.
 *
 * Userflow can't do this itself: its launcher and panel are the same fixed element
 * (`.userflowjs-resource-center`, 50x50 when collapsed, 360x401 when open) with the launcher
 * living inside its iframe. When the panel opens the launcher is simply gone, leaving the
 * header chevron as the only way out — so the button has to be ours.
 *
 * Userflow's panel is raised while the button is up so the two don't overlap.
 */

const RC_EVENT = 'resourceCenterChanged'
const STYLE_EL_ID = 'userflow-close-button-styles'
const BODY_OPEN_CLASS = 'ps-userflow-rc-open'
const BUTTON_CLASS = 'ps-userflow-close-button'

// Userflow's own offset from the right edge — matched so the button lines up with the panel it
// closes rather than with the other widgets' slightly different insets.
const RIGHT_OFFSET = 20

// Userflow renders its resource center at 1309. Sit at the same level: above app content and
// MUI dialogs, but in the same band as the panel this belongs to.
const Z_INDEX = 1309

function addStyles() {
  if (document.getElementById(STYLE_EL_ID)) {
    return
  }

  const style = document.createElement('style')
  style.id = STYLE_EL_ID
  // The panel-raise selector leads with `body.<class>` to outspecify Userflow's own
  // `.userflowjs-resource-center--fixed { bottom: 65px }`. It's scoped to the open state
  // because the collapsed launcher is the *same* element — raising it unconditionally would
  // leave the launcher floating too high.
  style.textContent = `
  body.${BODY_OPEN_CLASS} .userflowjs-resource-center--fixed {
    bottom: ${WIDGET_PANEL_BOTTOM}px;
  }
  .${BUTTON_CLASS} {
    position: fixed;
    bottom: ${WIDGET_BUTTON_BOTTOM}px;
    right: ${RIGHT_OFFSET}px;
    width: ${WIDGET_BUTTON_SIZE}px;
    height: ${WIDGET_BUTTON_SIZE}px;
    z-index: ${Z_INDEX};
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    border-radius: 50%;
    background-color: ${WIDGET_BUTTON_COLOR};
    box-shadow: ${WIDGET_BUTTON_SHADOW};
    cursor: pointer;
    transition: background-color 0.2s, transform 0.2s;
  }
  .${BUTTON_CLASS}:hover,
  .${BUTTON_CLASS}:focus-visible {
    background-color: ${WIDGET_BUTTON_COLOR_HOVER};
    transform: translateY(-1px);
  }
  .${BUTTON_CLASS} svg {
    width: 24px;
    height: 24px;
    fill: #fff;
  }`
  document.head.appendChild(style)
}

export default function UserflowCloseButton() {
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    addStyles()

    const sync = () => {
      try {
        setIsOpen(!!userflow.getResourceCenterState()?.isOpen)
      } catch {
        // Userflow isn't initialized (superadmins, missing token) — there's nothing to close.
        setIsOpen(false)
      }
    }

    sync()
    // Fires for every open/close regardless of what triggered it, so this covers Userflow's own
    // header chevron and the resource center closing to hand off to another widget.
    userflow.on(RC_EVENT, sync)

    return () => userflow.off(RC_EVENT, sync)
  }, [])

  useEffect(() => {
    document.body.classList.toggle(BODY_OPEN_CLASS, isOpen)
    return () => document.body.classList.remove(BODY_OPEN_CLASS)
  }, [isOpen])

  if (!isOpen) {
    return null
  }

  return (
    <button
      type="button"
      className={BUTTON_CLASS}
      aria-label="Close resource center"
      onClick={() => userflow.closeResourceCenter()}
    >
      <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
        <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.3 19.71 2.89 18.3 9.18 12 2.89 5.71 4.3 4.29l6.29 6.3 6.3-6.3z" />
      </svg>
    </button>
  )
}
